// The AI layer's rules (brief 1B-8, PLAN.md 4.11). One place in FlowCare talks to AI; these are
// the rules it follows. A feature runs only while AI is on for the company, the feature is on, its
// job has a provider set up and the budget allows. A call's cost comes from its tokens (or audio
// minutes) and the company's price rows, never from a price in code. At the warn % the Admins are
// told once a month; at the limit the company's choice applies: pause (the AI buttons say "AI
// paused until next month" and the work goes on by hand) or keep going (the Admins are alerted
// once). "Leave out names and ID numbers when the job doesn't need them" takes them out of
// writing help before anything is sent. AI never creates or changes a record: an answer is only
// shown, and a person keeps it or not. Pure: no database, no clock.

import { DEFAULT_TIME_ZONE, zonedDay } from "./week";

export type AiFeature = "dictation" | "writing" | "reading";
export type FeatureState = "on" | "off" | "paused" | "not_set_up";

/** Every feature in Settings › AI, in the mockup's order. Only the live ones can be switched on. */
export const AI_FEATURES: readonly {
  key: string;
  name: string;
  blurb: string;
  where: string;
  live: boolean;
}[] = [
  {
    key: "dictation",
    name: "Dictation",
    blurb: "The mic on every text box (up to 10 minutes)",
    where: "Every text box",
    live: true,
  },
  {
    key: "writing",
    name: "Writing help",
    blurb: "Clean up, shorten, draft a reply, rewrite as a clinical note",
    where: "Every text box",
    live: true,
  },
  {
    key: "reading",
    name: "Reading uploads",
    blurb: "Fills in details from credentials and IDs for a person to check",
    where: "Every credential upload",
    live: true,
  },
  {
    key: "scan",
    name: "Filing scans and faxes",
    blurb: "Says where a scan belongs; a person confirms",
    where: "Inbox › Scans",
    live: false,
  },
  {
    key: "inbox",
    name: "Sorting the inbox",
    blurb: "Labels mail, links it to the record, filters spam",
    where: "Inbox",
    live: false,
  },
  {
    key: "draft",
    name: "Visit and assessment drafts",
    blurb: "Dictate the visit, summary for the case manager, gap check",
    where: "Assessments, visit notes",
    live: false,
  },
  {
    key: "agent",
    name: "Recruiting agent",
    blurb: "Texts candidates and follows the company's rules",
    where: "Staffing › Recruiting",
    live: false,
  },
  {
    key: "ask",
    name: "Ask AI in help and training",
    blurb: "Answers questions and points to the right lesson",
    where: "Help, Training",
    live: false,
  },
];

export const LIVE_FEATURES: readonly AiFeature[] = ["dictation", "writing", "reading"];
export const isLiveFeature = (k: unknown): k is AiFeature =>
  typeof k === "string" && (LIVE_FEATURES as readonly string[]).includes(k);

/** The rules AI follows (mockup). The first is fixed; the rest are the company's choice. */
export const AI_RULES: readonly { key: string; label: string; fixed?: true }[] = [
  {
    key: "confirm",
    label: "AI never creates or changes a record by itself. A person confirms.",
    fixed: true,
  },
  {
    key: "drafts",
    label: "AI may prepare drafts (intake, credential, trip request) for a person to confirm",
  },
  {
    key: "send",
    label: "AI may send routine texts (reminders, shift offers) without a person pressing Send",
  },
  { key: "redact", label: "Leave out names and ID numbers when the job doesn't need them" },
  {
    key: "baa",
    label: "Use only AI services that signed a business associate agreement (HIPAA)",
  },
  {
    key: "learn",
    label: "Learn from corrections (where a scan really belonged, which label was right)",
  },
];
const RULE_KEYS = AI_RULES.filter((r) => !r.fixed).map((r) => r.key);

/** Who answers each job: Claude for writing and reading, speech to text for dictation. */
export type AiProviderKind = "claude" | "speech";

/** Which model does which job (Settings › AI). Model names, not prices: prices are data rows. */
export const AI_JOBS: readonly {
  key: string;
  name: string;
  provider: AiProviderKind;
  /** The live feature this job serves; none for the ones that come later. */
  feature: AiFeature | null;
}[] = [
  { key: "writing", name: "Writing help", provider: "claude", feature: "writing" },
  { key: "reading", name: "Reading uploads", provider: "claude", feature: "reading" },
  {
    key: "dictation",
    name: "Dictation (speech to text)",
    provider: "speech",
    feature: "dictation",
  },
  { key: "inbox", name: "Sorting the inbox", provider: "claude", feature: null },
  { key: "draft", name: "Visit and assessment drafts", provider: "claude", feature: null },
  { key: "agent", name: "Recruiting agent", provider: "claude", feature: null },
];

export const MODELS: Record<AiProviderKind, readonly { id: string; label: string }[]> = {
  claude: [
    { id: "claude-haiku-5-5", label: "Claude Haiku 5.5" },
    { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5" },
    { id: "claude-opus-5-5", label: "Claude Opus 5.5" },
  ],
  speech: [{ id: "gpt-4o-transcribe", label: "Speech to text" }],
};
/** Shown in the model lists, not yet selectable (brief 1B-8: per-company keys come later). */
export const OWN_KEY = "Own key";

export const modelLabel = (id: string) =>
  [...MODELS.claude, ...MODELS.speech].find((m) => m.id === id)?.label ?? id;

export const DEFAULT_MODELS: Record<AiFeature, string> = {
  writing: "claude-sonnet-5-5",
  reading: "claude-sonnet-5-5",
  dictation: "gpt-4o-transcribe",
};

/** The settings as the rules read them. */
export interface AiSettings {
  is_on: boolean;
  features: Partial<Record<string, boolean>>;
  rules: Partial<Record<string, boolean>>;
  budget_cents: number;
  warn_pct: number;
  at_limit: "pause" | "keep_going";
  models: Partial<Record<string, string>>;
}

export const modelFor = (s: Pick<AiSettings, "models">, feature: AiFeature) =>
  s.models[feature] ?? DEFAULT_MODELS[feature];

export const ruleOn = (s: Pick<AiSettings, "rules">, key: string) => s.rules[key] === true;

// ---- Money ---------------------------------------------------------------------------------------

/** The New York month a moment counts in, YYYY-MM. */
export const monthOf = (at: Date, zone = DEFAULT_TIME_ZONE) => zonedDay(at, zone).slice(0, 7);

/** A price row as the rules read it: micro-dollars per million tokens, or per audio minute. */
export interface Price {
  input_per_million: number | string;
  output_per_million: number | string;
  audio_per_minute: number | string;
}

export interface Usage {
  inputTokens?: number;
  outputTokens?: number;
  audioSeconds?: number;
}

/**
 * What a call cost, in micro-dollars (millionths of a dollar), from its tokens or audio minutes
 * and the model's price row. No price row, no cost: null, and the call says the price is missing.
 */
export function callCost(usage: Usage, price: Price | null | undefined): number | null {
  if (!price) return null;
  const n = (v: number | string) => Number(v) || 0;
  const tokens =
    ((usage.inputTokens ?? 0) * n(price.input_per_million) +
      (usage.outputTokens ?? 0) * n(price.output_per_million)) /
    1_000_000;
  const audio = ((usage.audioSeconds ?? 0) / 60) * n(price.audio_per_minute);
  return Math.round(tokens + audio);
}

/** This month's spend: the sum of the calls' costs (derived, never stored). */
export const spentOf = (calls: readonly { cost_micros: number | string }[]) =>
  calls.reduce((sum, c) => sum + (Number(c.cost_micros) || 0), 0);

/** A dollar amount for the screen, from micro-dollars: $38.20. */
export const usd = (micros: number) => `$${(micros / 1_000_000).toFixed(2)}`;

/** This month's spend against the limit, as a whole percent (0 when there's no limit). */
export const percentOf = (spentMicros: number, budgetCents: number) =>
  budgetCents > 0 ? Math.round((spentMicros / (budgetCents * 10_000)) * 100) : 0;

export const atLimit = (spentMicros: number, budgetCents: number) =>
  spentMicros >= budgetCents * 10_000;

/**
 * What the budget says after a call. `warn`: the spend is at or over the warn % (the Admins are
 * told once a month). `alert`: at the limit while the company keeps going (alerted once a month).
 * At the limit with pause, nothing more is sent: featureState() says paused.
 */
export function budgetNews(
  s: Pick<AiSettings, "budget_cents" | "warn_pct" | "at_limit">,
  spentMicros: number,
): { warn: boolean; alert: boolean } {
  const limit = s.budget_cents * 10_000;
  return {
    warn: spentMicros >= (limit * s.warn_pct) / 100,
    alert: s.at_limit === "keep_going" && atLimit(spentMicros, s.budget_cents),
  };
}

/** The one-a-month keys the Admins' notices carry, so each is told once (per Admin). */
export const noticeKey = (kind: "warn" | "limit", month: string, adminId: string) =>
  `ai_${kind}:${month}:${adminId}`;

// ---- Whether a feature runs ------------------------------------------------------------------------

/**
 * Whether a feature's buttons show and its calls go: off (AI off, the feature off, or not a live
 * feature), not set up (its job's provider has no key), paused (at the limit with pause), or on.
 * Anything but on or paused hides the buttons; paused shows them saying "AI paused until next
 * month".
 */
export function featureState(
  s: AiSettings,
  feature: string,
  spentMicros: number,
  providerReady: boolean,
): FeatureState {
  if (!s.is_on || !isLiveFeature(feature) || s.features[feature] !== true) return "off";
  if (!providerReady) return "not_set_up";
  if (s.at_limit === "pause" && atLimit(spentMicros, s.budget_cents)) return "paused";
  return "on";
}

export const PAUSED = "AI paused until next month";

// ---- Changing the settings --------------------------------------------------------------------------

type Changes = Partial<
  Pick<
    AiSettings,
    "is_on" | "features" | "rules" | "budget_cents" | "warn_pct" | "at_limit" | "models"
  >
>;

/**
 * Reads a change to Settings › AI in plain words. Only the live features switch; the fixed rule
 * can't be switched off; a model must be one of its job's; "Your own key" isn't selectable yet.
 */
export function readAiChange(
  input: Record<string, unknown> | null,
  current: AiSettings,
): { ok: true; value: Changes } | { ok: false; error: string } {
  if (!input) return { ok: false, error: "Nothing to change." };
  const v: Changes = {};
  if ("on" in input) {
    if (typeof input.on !== "boolean") return { ok: false, error: "On is yes or no." };
    v.is_on = input.on;
  }
  if ("features" in input) {
    const f = input.features as Record<string, unknown> | null;
    if (!f || typeof f !== "object") return { ok: false, error: "Features are on or off." };
    const next = { ...current.features };
    for (const [k, on] of Object.entries(f)) {
      if (!isLiveFeature(k)) {
        const known = AI_FEATURES.find((x) => x.key === k);
        return {
          ok: false,
          error: known ? `${known.name} comes later.` : `There's no feature called ${k}.`,
        };
      }
      if (typeof on !== "boolean") return { ok: false, error: "Features are on or off." };
      next[k] = on;
    }
    v.features = next;
  }
  if ("rules" in input) {
    const r = input.rules as Record<string, unknown> | null;
    if (!r || typeof r !== "object") return { ok: false, error: "Rules are on or off." };
    const next = { ...current.rules };
    for (const [k, on] of Object.entries(r)) {
      if (k === "confirm")
        return { ok: false, error: "That rule is always on. A person confirms every AI change." };
      if (!RULE_KEYS.includes(k)) return { ok: false, error: `There's no rule called ${k}.` };
      if (typeof on !== "boolean") return { ok: false, error: "Rules are on or off." };
      next[k] = on;
    }
    v.rules = next;
  }
  if ("budget" in input) {
    const b = Number(input.budget);
    if (input.budget === null || input.budget === "" || !Number.isFinite(b) || b < 0 || b > 1e6)
      return { ok: false, error: "The monthly limit is a dollar amount from 0 to 1,000,000." };
    v.budget_cents = Math.round(b * 100);
  }
  if ("warnPct" in input) {
    const w = Number(input.warnPct);
    if (!Number.isInteger(w) || w < 1 || w > 100)
      return { ok: false, error: "Warn me at is a percent from 1 to 100." };
    v.warn_pct = w;
  }
  if ("atLimit" in input) {
    if (input.atLimit !== "pause" && input.atLimit !== "keep_going")
      return { ok: false, error: "At the limit, pause or keep going." };
    v.at_limit = input.atLimit;
  }
  if ("models" in input) {
    const m = input.models as Record<string, unknown> | null;
    if (!m || typeof m !== "object") return { ok: false, error: "Pick a model for each job." };
    const next = { ...current.models };
    for (const [job, model] of Object.entries(m)) {
      const j = AI_JOBS.find((x) => x.key === job && x.feature);
      if (!j) return { ok: false, error: `There's no live job called ${job}.` };
      if (model === OWN_KEY) return { ok: false, error: "Own keys come later." };
      if (!MODELS[j.provider].some((x) => x.id === model))
        return { ok: false, error: `${String(model)} can't do ${j.name.toLowerCase()}.` };
      next[job] = model as string;
    }
    v.models = next;
  }
  if (Object.keys(v).length === 0) return { ok: false, error: "Nothing to change." };
  return { ok: true, value: v };
}

/** Reads a price change: dollars per million tokens, or per audio minute, and the as-of date. */
export function readPriceChange(
  input: Record<string, unknown> | null,
): { ok: true; value: Record<string, number | string> } | { ok: false; error: string } {
  if (!input) return { ok: false, error: "Nothing to change." };
  const v: Record<string, number | string> = {};
  const money: [string, string][] = [
    ["inputPerMillion", "input_per_million"],
    ["outputPerMillion", "output_per_million"],
    ["audioPerMinute", "audio_per_minute"],
  ];
  for (const [k, col] of money) {
    if (!(k in input)) continue;
    const d = Number(input[k]);
    if (input[k] === null || input[k] === "" || !Number.isFinite(d) || d < 0 || d > 10_000)
      return { ok: false, error: "A price is a dollar amount from 0 to 10,000." };
    v[col] = Math.round(d * 1_000_000);
  }
  if ("asOf" in input) {
    if (typeof input.asOf !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(input.asOf))
      return { ok: false, error: "As of is a date." };
    v.as_of = input.asOf;
  }
  if (Object.keys(v).length === 0) return { ok: false, error: "Nothing to change." };
  return { ok: true, value: v };
}

// ---- Writing help ------------------------------------------------------------------------------------

export type WritingAction = "clean" | "shorten" | "reply" | "clinical";

/** The AI menu on every text box. Draft a reply shows only where there's a conversation. */
export const WRITING_ACTIONS: readonly { key: WritingAction; label: string }[] = [
  { key: "clean", label: "Clean up spelling and grammar" },
  { key: "shorten", label: "Make it shorter" },
  { key: "reply", label: "Draft a reply" },
  { key: "clinical", label: "Rewrite as a clinical note" },
];
export const isWritingAction = (k: unknown): k is WritingAction =>
  WRITING_ACTIONS.some((a) => a.key === k);

/** What each action asks for. The text itself goes after it; nothing is kept. */
export const WRITING_ASK: Record<WritingAction, string> = {
  clean:
    "Fix the spelling, grammar and punctuation of the text below. Keep the meaning, the words and the tone; change nothing else.",
  shorten: "Make the text below shorter and plainer. Keep every fact, time, date and instruction.",
  reply:
    "Draft a short, friendly reply from the office to the last message of the conversation below. Use only facts in the conversation; promise nothing it doesn't say. If the person's own words are given, build on them.",
  clinical:
    "Rewrite the text below as an objective clinical note: what was observed and done, in plain past tense, without opinions. Add nothing that isn't in the text.",
};

export const WRITING_SYSTEM =
  "You help the office staff of a home care, staffing and medical transportation company write. Answer with the finished text only: no preamble, no quotes, no notes. Words in square brackets, like [Name 1] or [Number 1], stand for details left out; keep them exactly as they are.";

/** The most text writing help takes at once. */
export const WRITING_MAX = 8000;

// ---- Names and ID numbers --------------------------------------------------------------------------

/**
 * Whether a word is an ID number: SSNs, Medicaid, Medicare, license, card and member numbers,
 * phone numbers. Five or more digits, or letters and four or more digits joined by dashes
 * (TEST-CNA-0003). A year, a time, a count or an ISO date stays.
 */
function isIdNumber(word: string): boolean {
  if (/^\d{4}-\d{2}-\d{2}$/.test(word)) return false;
  const digits = word.replace(/\D/g, "").length;
  return digits >= 5 || (digits >= 4 && /[a-z]/i.test(word) && word.includes("-"));
}

/**
 * Takes names and ID numbers out of text before it goes to AI (rule "redact"): each becomes
 * [Name 1], [Number 1] and so on, the same one each time it appears. `restore` puts them back in
 * the answer, so the person sees the real words and AI never did.
 */
export function leaveOut(
  text: string,
  names: readonly string[],
): { text: string; restore: (answer: string) => string } {
  const swaps = new Map<string, string>();
  let out = text;
  let n = 0;
  // Longest names first, so "Lena Fairweather" goes before "Lena".
  const words = [...new Set(names.map((x) => x.trim()).filter((x) => x.length >= 2))].sort(
    (a, b) => b.length - a.length,
  );
  for (const name of words) {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
    if (!re.test(out)) continue;
    const tag = `[Name ${++n}]`;
    swaps.set(tag, name);
    out = out.replace(re, tag);
  }
  let k = 0;
  const seen = new Map<string, string>();
  out = out.replace(/[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*/g, (m) => {
    if (!isIdNumber(m)) return m;
    let tag = seen.get(m.toUpperCase());
    if (!tag) {
      tag = `[Number ${++k}]`;
      seen.set(m.toUpperCase(), tag);
      swaps.set(tag, m);
    }
    return tag;
  });
  return {
    text: out,
    restore: (answer) => {
      let a = answer;
      for (const [tag, real] of swaps) a = a.split(tag).join(real);
      return a;
    },
  };
}

/** The names a company's writing help leaves out: each person's full name and each part of it. */
export function namesToLeaveOut(people: readonly string[]): string[] {
  const all: string[] = [];
  for (const full of people) {
    const name = full.trim();
    if (!name) continue;
    all.push(name);
    for (const part of name.split(/\s+/)) if (part.length >= 3) all.push(part);
  }
  return all;
}

// ---- Dictation -------------------------------------------------------------------------------------

/** Up to 10 minutes a part; amber from 9 minutes, red (with a beep) for the last 30 seconds. */
export const DICTATION_MAX_SECONDS = 600;
export const DICTATION_AMBER_AT = 540;
export const DICTATION_RED_AT = 570;

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** What the recording clock shows after `seconds`, and whether to beep or stop. */
export function dictationClock(seconds: number): {
  text: string;
  level: "normal" | "amber" | "red";
  beep: boolean;
  stop: boolean;
} {
  const s = Math.max(0, Math.min(DICTATION_MAX_SECONDS, Math.floor(seconds)));
  return {
    text: `${clock(s)} / ${clock(DICTATION_MAX_SECONDS)}`,
    level: s >= DICTATION_RED_AT ? "red" : s >= DICTATION_AMBER_AT ? "amber" : "normal",
    beep: s === DICTATION_RED_AT,
    stop: s >= DICTATION_MAX_SECONDS,
  };
}

/**
 * Puts dictated words in a text box at the cursor (or the end), with a space either side as
 * needed. Answers the new value and where the cursor goes: after the words, so the next part is
 * added after them.
 */
export function insertDictation(
  value: string,
  at: number,
  words: string,
): { value: string; caret: number } {
  const w = words.trim();
  if (!w) return { value, caret: at };
  const i = Math.max(0, Math.min(value.length, at));
  const before = value.slice(0, i);
  const after = value.slice(i);
  const lead = before && !/\s$/.test(before) ? " " : "";
  const trail = after && !/^\s/.test(after) ? " " : "";
  const next = `${before}${lead}${w}${trail}${after}`;
  return { value: next, caret: before.length + lead.length + w.length };
}

/** Audio types a recording may arrive as. It's sent once for the words and never kept. */
export const AUDIO_TYPES = ["audio/webm", "audio/ogg", "audio/mp4", "audio/mpeg", "audio/wav"];
/** A 10-minute part at the browser's speech bit rate fits well inside one request. */
export const AUDIO_MAX_BYTES = 4_000_000;

// ---- Reading uploads -------------------------------------------------------------------------------

export type ReadField = "number" | "issued" | "expires" | "name";
export const READ_FIELDS: readonly ReadField[] = ["number", "issued", "expires", "name"];
export const READ_LABEL: Record<ReadField, string> = {
  number: "Number",
  issued: "Issued",
  expires: "Expires",
  name: "Name on the document",
};

/** File types AI can read itself: photos, screenshots, scans and PDFs. */
export const READABLE_TYPES: Record<string, "image" | "pdf"> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "application/pdf": "pdf",
};

/** The JSON shape reading answers in, for the fields the requirement asks for. */
export function readSchema(fields: readonly ReadField[]) {
  const properties: Record<string, { type: string[]; description: string }> = {};
  const describe: Record<ReadField, string> = {
    number: "The certificate, card, license or ID number, exactly as printed; null if none.",
    issued: "The issue date as YYYY-MM-DD; null if not printed.",
    expires: "The expiry date as YYYY-MM-DD; null if not printed.",
    name: "The holder's full name as printed; null if none.",
  };
  for (const f of fields) properties[f] = { type: ["string", "null"], description: describe[f] };
  return {
    type: "object",
    properties,
    required: [...fields],
    additionalProperties: false,
  };
}

/** What the AI is asked about an upload (not on screen). */
export const readAsk = (document: string, fields: readonly ReadField[]) =>
  `This is a ${document}. Read these fields from it: ${fields.map((f) => READ_LABEL[f].toLowerCase()).join(", ")}. Answer null for any you can't read clearly; never guess.`;

const DATE_US = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const DATE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A date as YYYY-MM-DD from what AI read, or null when it isn't a real date. */
export function readDate(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  let y: number, m: number, d: number;
  const iso = DATE_ISO.exec(t);
  const us = DATE_US.exec(t);
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (us) [y, m, d] = [Number(us[3]), Number(us[1]), Number(us[2])];
  else return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** What reading found, kept to the fields asked for and checked; nothing is saved from here. */
export function readAnswer(
  raw: unknown,
  fields: readonly ReadField[],
): Partial<Record<ReadField, string>> {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out: Partial<Record<ReadField, string>> = {};
  for (const f of fields) {
    const v = r[f];
    if (f === "issued" || f === "expires") {
      const d = readDate(v);
      if (d) out[f] = d;
    } else if (typeof v === "string" && v.trim())
      out[f] = v.trim().slice(0, f === "name" ? 120 : 60);
  }
  return out;
}

const plain = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1);

/**
 * Whether the name read from a document is the person's: their first and last name both appear
 * (any order, any case, middle names and initials ignored). Null when no name was read.
 */
export function nameMatches(read: string | undefined, first: string, last: string): boolean | null {
  if (!read) return null;
  const words = new Set(plain(read));
  const want = [...plain(first), ...plain(last)];
  return want.length > 0 && want.every((w) => words.has(w));
}

/**
 * The fields a person checked and saved after AI filled them (sent with the save). Only known
 * field names, each once.
 */
export function readChecked(v: unknown): ReadField[] {
  if (!Array.isArray(v)) return [];
  return READ_FIELDS.filter((f) => v.includes(f));
}
